import React, { useCallback, useEffect, useState } from "react";

import { ApiContenido, CategoriaPeso, EsquemaDelNivel } from "../../../../services/v2/lmsContenido.service";
import { Icono, mensajeDeError, useAlVolver } from "../comun";
import "../portalClase.css";
import "./contenido.css";

/*
  Pestaña Evaluación (Portal de Clase, Parte A2, 2026-10-17): cómo se calcula la nota. Los pesos se definen en el curso (el
  default), el módulo los puede cambiar y el horario puede cambiar lo del módulo; un override reemplaza la lista entera. Las
  categorías de cada nivel suman EXACTAMENTE 100 %: no se puede guardar si no. El interruptor de abajo (solo en el curso) deja
  que el maestro cambie lo heredado de su horario.
*/

const centesimas = (n: number): number => Math.round((Number(n) || 0) * 100)
const fmt = (n: number): string => String(Math.round(n * 100) / 100)
const NOMBRE_NIVEL = { curso: "El curso", modulo: "El módulo", horario: "Este horario" } as const

interface Fila { name: string; weight: string; esAsistencia: boolean }
const aFilas = (c: CategoriaPeso[]): Fila[] => c.map((x) => ({ name: x.name, weight: String(x.weight), esAsistencia: x.esAsistencia }))

export const PanelEvaluacion: React.FC<{ api: ApiContenido; compacto?: boolean; onCambio?: () => void }> = ({ api, compacto, onCambio }) => {
	const [datos, setDatos] = useState<EsquemaDelNivel | null>(null)
	const [filas, setFilas] = useState<Fila[] | null>(null)   // null = solo lectura
	const [error, setError] = useState<string | null>(null)
	const [aviso, setAviso] = useState<string | null>(null)
	const [ocupado, setOcupado] = useState<boolean>(false)

	const cargar = useCallback(() => {
		api.esquema().then((d) => { setDatos(d); setError(null) }).catch((e) => setError(mensajeDeError(e, "No pudimos cargar los pesos.")))
	}, [api])
	useEffect(() => { setDatos(null); setFilas(null); cargar() }, [cargar])
	useAlVolver(() => { if (!filas) cargar() })

	if (error && !datos) return <div className="pc pcEnFicha"><div className="pcAviso pcOro" role="alert">{error}</div></div>
	if (!datos) return <div className="pc pcEnFicha"><div className="pcCargando">Cargando…</div></div>

	const nivel = api.nivel
	const aplicar = (d: EsquemaDelNivel, texto?: string) => { setDatos(d); setFilas(null); setError(null); setAviso(texto || null); if (onCambio) onCambio() }
	const total = filas ? filas.reduce((s, f) => s + centesimas(Number(f.weight)), 0) : centesimas(datos.effective.total)
	const sumaOk = total === 10000
	//Only one category can come from attendance: it behaves as a radio that can also be cleared (click the marked one again).
	const unaAsistencia = (i: number) => setFilas((fs) => (fs || []).map((f, j) => ({ ...f, esAsistencia: j === i ? !f.esAsistencia : false })))

	const guardar = async () => {
		if (!filas || ocupado) return
		//Changing which category is attendance changes what counts (its tasks stop counting): say it before saving (QA M11).
		const antes = datos.effective.categories.find((c) => c.esAsistencia)?.name.trim().toLowerCase() || ""
		const nueva = filas.find((f) => f.esAsistencia)
		const ahora = nueva ? nueva.name.trim().toLowerCase() : ""
		if (antes !== ahora && !window.confirm(nueva
			? "\"" + nueva.name.trim() + "\" se tomará de la asistencia: sus tareas dejan de contar en la nota. ¿Guardar?"
			: "Ninguna categoría se tomará de la asistencia. ¿Guardar?")) return
		setOcupado(true); setError(null)
		try {
			aplicar(await api.guardarEsquema(filas.map((f) => ({ name: f.name.trim(), weight: Number(f.weight), esAsistencia: f.esAsistencia }))), "Pesos Guardados")
		} catch (e) { setError(mensajeDeError(e)) } finally { setOcupado(false) }
	}
	const quitar = async () => {
		if (!window.confirm("¿Volver a los pesos heredados? Se pierde lo que cambiaste aquí.")) return
		setOcupado(true); setError(null)
		try { aplicar(await api.quitarEsquema(), "Vuelve A Heredar Los Pesos") } catch (e) { setError(mensajeDeError(e)) } finally { setOcupado(false) }
	}
	const cambiarInterruptor = async () => {
		setOcupado(true); setError(null)
		try { aplicar(await api.permiteMaestro(!datos.allowTeacherOverride), !datos.allowTeacherOverride ? "El Maestro Ya Puede Cambiar Lo Heredado" : "El Maestro Ya No Puede Cambiar Lo Heredado") } catch (e) { setError(mensajeDeError(e)) } finally { setOcupado(false) }
	}

	const estadistica = (nombre: string) => datos.categoryStats.find((c) => c.name.toLowerCase() === nombre.toLowerCase())

	return (
		<div className="pc pcEnFicha">
			<div className="pcCont">
				<div className="pcContCabeza">
					<div className="pcContTitulo" style={compacto ? { fontSize: 17 } : undefined}>Cómo Se Calcula La Nota</div>
					<span className="pcChip pcChipBorde">{datos.effective.sourceLabel}</span>
					<span className={"pcTotal" + (sumaOk ? "" : " pcOroTexto")}>Total {fmt(total / 100)} %</span>
				</div>
				{aviso ? <div className="pcAviso" role="status" style={{ color: "var(--pc-oro-texto)" }}>{aviso}</div> : null}
				{error ? <div className="pcAviso pcOro" role="alert">{error}</div> : null}

				{filas === null ? (
					<>
						<div className="pcEsquemaLista">
							{datos.effective.categories.map((c) => {
								const st = estadistica(c.name)
								return (
									<div key={c.name}>
										<div className="pcEsquemaFila"><span className="pcNombre">{c.name}{c.esAsistencia ? <span className="pcTenue pcChico"> · Se Toma De La Asistencia</span> : null}</span><b>{fmt(c.weight)} %</b></div>
										{st && st.fixed > 0 ? <div className="pcAyuda" style={{ padding: "2px 14px 0" }}>Tareas con peso fijo: {fmt(st.fixed)} % de {fmt(c.weight)} %{st.missing > 0 ? "" : ""}</div> : null}
										{st && st.missing > 0 ? <div className="pcAyuda pcOroTexto" style={{ padding: "0 14px" }}>Faltan {fmt(st.missing)} %</div> : null}
									</div>
								)
							})}
						</div>
						{datos.warnings.filter((w) => w.type === "sinCategoria").map((w, i) => <div className="pcNota pcNotaOro" key={i}>{w.message}</div>)}
						{datos.errors.map((m, i) => <div className="pcNota pcNotaOro" key={i}>{m}</div>)}
						{datos.canEdit ? (
							<div className="pcRedactorBotones">
								<button type="button" className="pcBtn pcBtnP" disabled={ocupado} onClick={() => { setFilas(aFilas(datos.effective.categories)); setAviso(null) }}>Cambiar Pesos</button>
								{nivel !== "curso" && datos.own ? <button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={quitar}>Volver A Lo Heredado</button> : null}
							</div>
						) : <div className="pcNota">{nivel === "horario" ? "Los pesos de esta clase los cambia Servicios Académicos." : "No tienes permiso para cambiar los pesos."}</div>}
						{datos.canEdit && nivel === "curso" ? (
							<div className="pcInterruptorFila" style={{ borderTop: "1px solid var(--pc-filo-claro)" }}>
								<span>El Maestro Puede Cambiar Pesos Y Contenido Heredado De Su Horario<br /><span className="pcAyuda">Apagado: solo Servicios Académicos lo cambia. El maestro siempre puede agregar sus propias piezas.</span></span>
								<button type="button" role="switch" aria-checked={datos.allowTeacherOverride} aria-label="El maestro puede cambiar lo heredado de su horario" disabled={ocupado}
									className={"pcSw" + (datos.allowTeacherOverride ? " pcSwOn" : "")} onClick={cambiarInterruptor} />
							</div>
						) : null}
					</>
				) : (
					<>
						<div className="pcAyuda">{NOMBRE_NIVEL[nivel]} {nivel === "curso" ? "define" : "reemplaza"} la lista entera. Las categorías deben sumar exactamente 100 %. Las tareas guardan el nombre de su categoría. La marcada como Asistencia se calcula con la asistencia (solo una; sus tareas no cuentan).</div>
						<div className="pcEsquemaLista">
							{filas.map((f, i) => (
								<div className="pcEsquemaFila" key={i}>
									<input className="pcContInput pcNombreInput" aria-label={"Nombre de la categoría " + (i + 1)} value={f.name} maxLength={80} onChange={(e) => setFilas(filas.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
									<input className="pcContInput pcPesoInput" type="number" inputMode="decimal" aria-label={"Peso de " + (f.name || "la categoría " + (i + 1))} min={0} max={100} step={0.5} value={f.weight} onChange={(e) => setFilas(filas.map((x, j) => (j === i ? { ...x, weight: e.target.value } : x)))} />
									<span aria-hidden="true">%</span>
									<label className="pcEsAsist"><input type="radio" name="pcCategoriaAsistencia" checked={f.esAsistencia} onChange={() => undefined} onClick={() => unaAsistencia(i)} />Asistencia</label>
									<button type="button" className="pcIb" style={{ width: 40, height: 40 }} aria-label={"Quitar " + (f.name || "categoría")} disabled={filas.length <= 1} onClick={() => setFilas(filas.filter((_, j) => j !== i))}><Icono n="cerrar" size={16} /></button>
								</div>
							))}
						</div>
						<div className="pcRedactorBotones" style={{ alignItems: "center" }}>
							<button type="button" className="pcBtn pcBtnS pcBtnChico" disabled={filas.length >= datos.maxCategories} onClick={() => setFilas([...filas, { name: "", weight: "0", esAsistencia: false }])}>Agregar Categoría</button>
							<span className={"pcTotal" + (sumaOk ? "" : " pcOroTexto")} role="status">
								{sumaOk ? "Suma 100 %" : total < 10000 ? "Suma " + fmt(total / 100) + " %: faltan " + fmt((10000 - total) / 100) + " %" : "Suma " + fmt(total / 100) + " %: sobran " + fmt((total - 10000) / 100) + " %"}
							</span>
						</div>
						<div className="pcRedactorPie"><div className="pcDerecha">
							<button type="button" className="pcBtn pcBtnS" onClick={() => { setFilas(null); setError(null) }}>Cancelar</button>
							<button type="button" className="pcBtn pcBtnP" disabled={!sumaOk || ocupado || filas.some((f) => f.name.trim() === "")} onClick={guardar}>Guardar Pesos</button>
						</div></div>
					</>
				)}
			</div>
		</div>
	)
}

export default PanelEvaluacion
